import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { PageHeader } from '../../components/ui/PageHeader';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { EmptyState } from '../../components/ui/EmptyState';
import { StatusDot } from '../../components/ui/StatusDot';
import { Button } from '../../components/ui/Button';
import { Modal } from '../../components/ui/Modal';
import { Badge } from '../../components/ui/Badge';
import { InlineActions } from '../../components/ui/InlineActions';

import {
  AlertCircle,
  CheckCircle,
  Globe,
  Lock,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Typy ─────────────────────────────────────────────────────────────────────
interface Profile {
  id?: number;
  name: string;
  type: 'alza' | 'planeo' | 'imobil';
  username: string;
  status: 'active' | 'verified' | 'blocked' | 'expired';
  last_used?: string;
  success_count: number;
  fail_count: number;
  created_at: string;
}

interface Job {
  id?: number;
  profile_id: number;
  url: string;
  action: string;
  status: 'pending' | 'running' | 'success' | 'failed';
  started_at?: string;
  ended_at?: string;
}

// ── Profily Tab ──────────────────────────────────────────────────────────────
function ProfilesTab() {
  const { t } = useTranslation();
  const [showCreate, setShowCreate] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['scraping-profiles'],
    queryFn: () =>
      api.mcp<{ ok: boolean; profiles: Profile[] }>(
        'scraping.profile_list',
        {},
        { silent: true },
      ),
  });

  const queryClient = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.mcp('scraping.profile_delete', { profileId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraping-profiles'] });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.mcp('scraping.profile_verify', { profileId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraping-profiles'] });
    },
  });

  const rotateMutation = useMutation({
    mutationFn: async (id: number) => {
      await api.mcp('scraping.profile_rotate', { profileId: id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraping-profiles'] });
    },
  });

  const handleDelete = async (id: number) => {
    if (await themedConfirm(t('scraping.confirmDeleteProfile'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleVerify = (id: number) => {
    verifyMutation.mutate(id);
  };

  const handleRotate = async (id: number) => {
    if (await themedConfirm(t('scraping.confirmRotateProfile'))) {
      rotateMutation.mutate(id);
    }
  };

  if (isLoading) return <div className='text-gray-400'>{t('scraping.loadingProfiles')}</div>;
  if (error) {
    return (
      <div className='text-red-400'>
        {t('scraping.errorPrefix', { message: (error as Error).message })}
      </div>
    );
  }

  const profiles = data?.profiles || [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-sm font-medium text-gray-400'>
          {t('scraping.profilesList', { count: profiles.length })}
        </h3>
        <Button size='sm' onClick={() => setShowCreate(true)} icon={<Plus size={14} />}>
          {t('scraping.newProfile')}
        </Button>
      </div>

      {profiles.length === 0
        ? (
          <EmptyState
            icon={<Globe size={24} />}
            title={t('scraping.noProfiles')}
            description={t('scraping.noProfilesDesc')}
            action={
              <Button size='sm' onClick={() => setShowCreate(true)} icon={<Plus size={14} />}>
                {t('scraping.newProfile')}
              </Button>
            }
          />
        )
        : (
          <div className='bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden'>
            {profiles.map((profile) => (
              <div
                key={profile.id}
                className='flex items-center justify-between px-4 py-3 border-b border-gray-800 last:border-0 hover:bg-gray-800/50 transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <Badge
                    variant={profile.type === 'alza'
                      ? 'success'
                      : profile.type === 'planeo'
                      ? 'warning'
                      : 'info'}
                  >
                    {profile.type.toUpperCase()}
                  </Badge>
                  <div>
                    <p className='text-sm font-medium text-white'>{profile.name}</p>
                    <p className='text-xs text-gray-500'>{profile.username}</p>
                  </div>
                </div>

                <div className='flex items-center gap-4'>
                  <div className='flex items-center gap-2 text-xs text-gray-500'>
                    <span className='flex items-center gap-1'>
                      <CheckCircle size={12} className='text-green-500' />
                      {profile.success_count}
                    </span>
                    <span className='flex items-center gap-1'>
                      <AlertCircle size={12} className='text-red-500' />
                      {profile.fail_count}
                    </span>
                  </div>

                  <StatusDot status={profile.status} withLabel />

                  <InlineActions
                    actions={[
                      {
                        label: t('scraping.verify'),
                        icon: <CheckCircle size={14} />,
                        onClick: () => handleVerify(profile.id || 0),
                        loading: verifyMutation.isPending,
                      },
                      {
                        label: t('scraping.rotate'),
                        icon: <RefreshCw size={14} />,
                        onClick: () => handleRotate(profile.id || 0),
                        loading: rotateMutation.isPending,
                      },
                      {
                        label: t('scraping.delete'),
                        icon: <Trash2 size={14} />,
                        onClick: () => handleDelete(profile.id || 0),
                        loading: deleteMutation.isPending,
                        danger: true,
                      },
                    ]}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Modal pro nový profil */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title={t('scraping.newScrapingProfile')}
        size='md'
      >
        <ProfileForm
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            refetch();
          }}
        />
      </Modal>
    </div>
  );
}

// ── Formulář profilu ─────────────────────────────────────────────────────────
function ProfileForm({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [type, setType] = useState<'alza' | 'planeo' | 'imobil'>('alza');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userAgent, setUserAgent] = useState('');

  const mutation = useMutation({
    mutationFn: async (data: {
      name: string;
      type: string;
      username: string;
      password?: string;
      userAgent?: string;
    }) => {
      await api.mcp('scraping.profile_create', data);
    },
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ name, type, username, password, userAgent });
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <div>
        <label className='block text-xs font-medium text-gray-400 mb-1'>
          {t('scraping.name')}
        </label>
        <input
          type='text'
          value={name}
          onChange={(e) => setName(e.target.value)}
          className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none'
          placeholder={t('scraping.namePlaceholder')}
          required
        />
      </div>

      <div>
        <label className='block text-xs font-medium text-gray-400 mb-1'>
          {t('scraping.type')}
        </label>
        <div className='flex gap-2'>
          {(['alza', 'planeo', 'imobil'] as const).map((pt) => (
            <button
              key={pt}
              type='button'
              onClick={() => setType(pt)}
              className={`flex-1 py-2 rounded-xl text-sm font-medium border transition-colors ${
                type === pt
                  ? 'bg-blue-600/20 border-blue-500/30 text-blue-400'
                  : 'bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {pt.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className='block text-xs font-medium text-gray-400 mb-1'>
          {t('scraping.username')}
        </label>
        <div className='relative'>
          <Lock size={16} className='absolute left-3 top-2.5 text-gray-500' />
          <input
            type='text'
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className='w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none'
            placeholder='username@example.com'
            required
          />
        </div>
      </div>

      <div>
        <label className='block text-xs font-medium text-gray-400 mb-1'>
          {t('scraping.password')}
        </label>
        <div className='relative'>
          <Lock size={16} className='absolute left-3 top-2.5 text-gray-500' />
          <input
            type='password'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className='w-full bg-gray-800 border border-gray-700 rounded-xl pl-9 pr-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none'
            placeholder='••••••••'
          />
        </div>
      </div>

      <div>
        <label className='block text-xs font-medium text-gray-400 mb-1'>
          {t('scraping.userAgentOptional')}
        </label>
        <input
          type='text'
          value={userAgent}
          onChange={(e) => setUserAgent(e.target.value)}
          className='w-full bg-gray-800 border border-gray-700 rounded-xl px-3 py-2 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none'
          placeholder='Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36...'
        />
      </div>

      <div className='flex items-center justify-end gap-2 pt-2'>
        <Button type='button' variant='secondary' size='sm' onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button type='submit' size='sm' loading={mutation.isPending}>
          {t('scraping.createProfile')}
        </Button>
      </div>
    </form>
  );
}

// ── Joby Tab ─────────────────────────────────────────────────────────────────
function JobsTab() {
  const { t } = useTranslation();
  // Jobs list: no MCP tool yet — REST until scraping.jobs_* lands
  const { data, isLoading, error } = useQuery({
    queryKey: ['scraping-jobs'],
    queryFn: () => api.mcp<{ ok: boolean; jobs: Job[] }>('scraping.jobs_list', {}),
  });

  const jobs = data?.jobs || [];

  if (isLoading) return <div className='text-gray-400'>{t('scraping.loadingJobs')}</div>;
  if (error) {
    return (
      <div className='text-red-400'>
        {t('scraping.errorPrefix', { message: (error as Error).message })}
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <h3 className='text-sm font-medium text-gray-400'>
          {t('scraping.recentJobs', { count: jobs.length })}
        </h3>
      </div>

      {jobs.length === 0
        ? (
          <EmptyState
            icon={<RefreshCw size={24} />}
            title={t('scraping.noJobs')}
            description={t('scraping.noJobsDesc')}
          />
        )
        : (
          <div className='bg-gray-900/50 border border-gray-800 rounded-2xl overflow-hidden'>
            {jobs.map((job) => (
              <div
                key={job.id}
                className='flex items-center justify-between px-4 py-3 border-b border-gray-800 last:border-0 hover:bg-gray-800/50 transition-colors'
              >
                <div className='flex items-center gap-3'>
                  <div className='flex flex-col'>
                    <p className='text-sm font-medium text-white truncate max-w-[200px]'>
                      {job.url}
                    </p>
                    <p className='text-xs text-gray-500'>{job.action}</p>
                  </div>
                </div>

                <div className='flex items-center gap-4'>
                  <StatusDot status={job.status} withLabel />

                  <div className='text-xs text-gray-500'>
                    {job.started_at && new Date(job.started_at).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

// ── Scraping Page ────────────────────────────────────────────────────────────
export default function ScrapingPage() {
  const { t } = useTranslation();
  return (
    <div className='space-y-6'>
      <PageHeader
        title={t('scraping.pageTitle')}
        description={t('scraping.pageDescription')}
      />

      <div className='bg-gray-900/30 border border-gray-800 rounded-2xl p-6'>
        <Tabs defaultTab='profiles'>
          <TabList>
            <Tab id='profiles' icon={<Globe size={16} />}>
              {t('scraping.tabProfiles')}
            </Tab>
            <Tab id='jobs' icon={<RefreshCw size={16} />}>
              {t('scraping.tabJobs')}
            </Tab>
          </TabList>

          <TabPanel id='profiles'>
            <ProfilesTab />
          </TabPanel>

          <TabPanel id='jobs'>
            <JobsTab />
          </TabPanel>
        </Tabs>
      </div>
    </div>
  );
}
